import { apiFetch as fetch } from './apiConfig';
import { getAdminToken } from './adminService';

export interface AIBusinessSummary {
  totalBookings: number;
  totalRevenue: number;
  platformCommission: number; // 20%
  companionPayouts: number; // 80%
  aiAlertsCount: number;
  activeCompanions: number;
  activeClients: number;
  averageBookingValue: number;
  completionRate: number;
  aiEngineStatus: {
    status: 'ONLINE' | 'DEGRADED';
    model: string;
    latencyMs: number;
    lastAuditCheck: string;
  };
}

export interface AICategoryBreakdown {
  category: string;
  bookingCount: number;
  revenue: number;
  sharePercent: number;
  growthPercent: number;
  demandTrend: 'RISING' | 'STABLE' | 'SEASONAL';
}

export interface AIBusinessRecommendation {
  id: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  category: 'REVENUE' | 'CAPACITY' | 'PRICING' | 'SAFETY';
  title: string;
  impact: string;
  description: string;
  actionableStep: string;
}

export interface AIBusinessIntelligenceData {
  summary: AIBusinessSummary;
  categories: AICategoryBreakdown[];
  timeframeMetrics: {
    period: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
    revenue: number;
    bookings: number;
    projectedGrowth: number;
  };
  recommendations: AIBusinessRecommendation[];
  historicalVsProjected: {
    period: string;
    actualRevenue: number;
    projectedRevenue?: number;
    actualBookings: number;
    projectedBookings?: number;
  }[];
}

export interface AIFraudAlert {
  id: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  anomalyType:
    | 'RAPID_BOOKING_BURST'
    | 'PAYMENT_ABUSE_SUSPECTED'
    | 'SUSPICIOUS_ACCOUNT_PROFILE'
    | 'FAILED_OTP_PATTERN'
    | 'LOCATION_GEO_MISMATCH'
    | 'DUPLICATE_PAYMENT_DEVICE';
  title: string;
  description: string;
  timestamp: string;
  accountId: string;
  accountName: string;
  accountRole: 'client' | 'companion';
  confidenceScore: number; // e.g. 92
  evidence: string[];
  status: 'OPEN' | 'REVIEWED' | 'DISMISSED' | 'ACTION_TAKEN';
  adminNotes?: string;
  suggestedAction: string;
}

export interface AICityDemand {
  city: string;
  demandLevel: 'VERY_HIGH' | 'HIGH' | 'MODERATE' | 'LOW';
  projectedBookingsNextWeek: number;
  currentActiveCompanions: number;
  companionDeficit: number;
  topCategories: string[];
  surgeMultiplier: number;
}

export interface AIDemandForecastData {
  forecastPeriod: string;
  cities: AICityDemand[];
  peakBookingHours: string[];
  weekendSurgeIndex: number;
  overallConfidenceScore: number;
  limitationsNotice: string;
}

export interface AISmartMatchResult {
  companionId: string;
  name: string;
  city: string;
  avatarUrl?: string;
  hourlyRate: number;
  rateOneThirdDay?: number;
  compatibilityScore: number; // 0-100
  scoringBreakdown: {
    locationProximity: number; // e.g. 95
    serviceExpertise: number; // e.g. 90
    ratingTrust: number; // e.g. 98
    availabilityFit: number; // e.g. 85
  };
  services: string[];
  isDriverApproved?: boolean;
  matchReason: string;
}

export interface AIPricingAdviceItem {
  serviceCategory: string;
  city: string;
  currentAvgHourly: number;
  recommendedFloorRate: number;
  recommendedHourlyRange: { min: number; optimal: number; max: number };
  package1_3DayRateRecommended: number;
  packageHalfDayRateRecommended: number;
  packageFullDayRateRecommended: number;
  demandStrength: 'VERY_STRONG' | 'MODERATE' | 'REDUCING';
  expectedConversionBoost: string;
  justification: string;
}

export interface AISupportDisputeItem {
  id: string;
  ticketNumber: string;
  clientName: string;
  companionName?: string;
  category: 'BOOKING_ISSUE' | 'PAYMENT_DISPUTE' | 'COMPANION_NO_SHOW' | 'SAFETY_HARASSMENT' | 'REFUND_REQUEST';
  priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
  summary: string;
  originalMessage: string;
  sentiment: 'CRITICAL' | 'NEGATIVE' | 'NEUTRAL';
  aiSuggestedReply: string;
  replyApprovedByAdmin: boolean;
  status: 'PENDING_REVIEW' | 'ESCALATED' | 'RESOLVED';
  createdAt: string;
}

export interface AIDocumentPrescreenResult {
  companionId: string;
  companionName: string;
  documentType: 'DRIVING_LICENCE' | 'AADHAAR' | 'PASSPORT';
  overallStatus: 'LOW_RISK' | 'NEEDS_REVIEW' | 'SUSPICIOUS';
  confidenceScore: number;
  findings: {
    field: string;
    value: string;
    status: 'VALID' | 'FLAGGED' | 'BLURRY_OR_UNCLEAR';
    remark: string;
  }[];
  aiRecommendation: 'PROCEED_TO_APPROVE' | 'REQUEST_REUPLOAD' | 'REJECT_SUSPICIOUS';
  summaryText: string;
  humanSignoffRequired: boolean;
}

export interface AIChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  dataGrounded?: boolean;
  sources?: string[];
}

export interface AIAuditLogItem {
  id: string;
  timestamp: string;
  adminId: string;
  actionType: string;
  details: string;
  aiModel: string;
  tokensUsed?: number;
  latencyMs?: number;
}

// =========================================================================
// API CLIENT CALLS (Bearer Token / x-admin-token automatically managed)
// =========================================================================

function getAdminAuthHeaders(): Record<string, string> {
  const token = getAdminToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['x-admin-token'] = token;
  }
  return headers;
}

export async function fetchAIDashboardSummary(): Promise<{ success: boolean; data?: AIBusinessSummary; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/dashboard-summary', {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch AI dashboard summary' };
  }
}

export async function fetchAIBusinessIntelligence(period: 'WEEKLY' | 'MONTHLY' | 'YEARLY' = 'MONTHLY'): Promise<{
  success: boolean;
  data?: AIBusinessIntelligenceData;
  error?: string;
}> {
  try {
    const res = await fetch(`/api/admin/ai/business-intelligence?period=${period}`, {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch AI business intelligence' };
  }
}

export async function fetchAIFraudAlerts(): Promise<{ success: boolean; alerts?: AIFraudAlert[]; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/fraud-alerts', {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch AI fraud alerts' };
  }
}

export async function actionAIFraudAlert(
  alertId: string,
  action: 'REVIEW' | 'DISMISS' | 'FLAG_ACCOUNT' | 'ADD_NOTE',
  notes?: string
): Promise<{ success: boolean; alert?: AIFraudAlert; message?: string; error?: string }> {
  try {
    const res = await fetch(`/api/admin/ai/fraud-alerts/${alertId}/action`, {
      method: 'POST',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify({ action, notes }),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to update fraud alert' };
  }
}

export async function fetchAIDemandForecast(): Promise<{ success: boolean; data?: AIDemandForecastData; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/demand-forecast', {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch demand forecast' };
  }
}

export async function simulateSmartMatching(criteria: {
  category: string;
  city: string;
  budgetTier?: string;
  language?: string;
}): Promise<{ success: boolean; matches?: AISmartMatchResult[]; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/smart-matching', {
      method: 'POST',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify(criteria),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to simulate smart matching' };
  }
}

export async function fetchAIPricingAdvice(): Promise<{ success: boolean; items?: AIPricingAdviceItem[]; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/pricing-advisor', {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch pricing advice' };
  }
}

export async function fetchAISupportDisputes(): Promise<{ success: boolean; disputes?: AISupportDisputeItem[]; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/support-disputes', {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch support disputes' };
  }
}

export async function draftAISupportReply(
  disputeId: string,
  instruction?: string
): Promise<{ success: boolean; updatedDraft?: string; dispute?: AISupportDisputeItem; error?: string }> {
  try {
    const res = await fetch(`/api/admin/ai/support-disputes/${disputeId}/draft-reply`, {
      method: 'POST',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify({ instruction }),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to generate AI support draft' };
  }
}

export async function prescreenDocumentWithAI(docData: {
  companionId: string;
  documentType: string;
  licenceNumber?: string;
  expiryDate?: string;
  frontImageUrl?: string;
  backImageUrl?: string;
}): Promise<{ success: boolean; prescreenResult?: AIDocumentPrescreenResult; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/document-prescreen', {
      method: 'POST',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify(docData),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to prescreen document' };
  }
}

export async function sendAIChatQuestion(
  message: string,
  history: { role: 'user' | 'assistant'; text: string }[] = []
): Promise<{ success: boolean; reply?: string; groundedSources?: string[]; latencyMs?: number; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/chat', {
      method: 'POST',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify({ message, history }),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to communicate with AI Assistant' };
  }
}

export async function fetchAIAuditLogs(): Promise<{ success: boolean; logs?: AIAuditLogItem[]; error?: string }> {
  try {
    const res = await fetch('/api/admin/ai/audit-logs', {
      headers: getAdminAuthHeaders(),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to fetch AI audit logs' };
  }
}
